/* ── AJAX filter swap helpers ────────────────────────────────────── */

function plpBuildFilterUrl(form) {
  const params = new URLSearchParams(new FormData(form));
  /* Carry over passthrough params from the current URL that the form
     doesn't own (preview_theme_id, _ab, _sc, etc.). */
  const owned = new Set(params.keys());
  new URLSearchParams(window.location.search).forEach((val, key) => {
    if (!owned.has(key)) {
      params.set(key, val);
      owned.add(key);
    }
  });
  return `${form.action}?${params}`;
}

async function plpFetchAndSwap(url, sectionEl) {
  const sectionId = sectionEl.dataset.sectionId;
  sectionEl.classList.add('is-loading');

  const fetchUrl = new URL(url, window.location.origin);
  fetchUrl.searchParams.set('sections', sectionId);

  try {
    const res = await fetch(fetchUrl.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    const html = data[sectionId];
    if (!html) throw new Error('Section missing from response');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newSection = doc.querySelector(`[data-section-id="${sectionId}"]`);
    if (newSection) {
      sectionEl.replaceWith(newSection);
      /* Show the float filter button in the freshly-rendered section. */
      const floatBtn = newSection.querySelector('[data-plp-float-btn]');
      if (floatBtn) setTimeout(() => floatBtn.classList.add('is-visible'), 80);
    }
    return true;
  } catch (err) {
    console.error('[plp-collection-grid] Filter swap failed, falling back', err);
    sectionEl.classList.remove('is-loading');
    return false;
  }
}

/* ── Web Components ──────────────────────────────────────────────── */

if (!customElements.get('plp-collection-toolbar')) {
  class PlpCollectionToolbar extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const trigger = event.target.closest('[data-plp-drawer-open]');
      if (!trigger) return;
      const section = this.closest('[data-section-id]');
      if (!section) return;
      const drawer = section.querySelector('[data-plp-drawer]');
      if (!drawer || typeof drawer.open !== 'function') return;
      drawer.open(trigger);
    };
  }

  customElements.define('plp-collection-toolbar', PlpCollectionToolbar);
}

if (!customElements.get('plp-collection-drawer')) {
  class PlpCollectionDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-plp-drawer-panel]');
      this.overlay = this.querySelector('[data-plp-drawer-overlay]');
      this.closeBtn = this.querySelector('[data-plp-drawer-close]');
      this.form = this.querySelector('[data-plp-drawer-form]');
      this.lastTrigger = null;

      this.onOverlayClick = () => this.close();
      this.onCloseClick = () => this.close();
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && !this.hasAttribute('hidden')) this.close();
      };
      this.onFormSubmit = this._onFormSubmit.bind(this);

      if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onCloseClick);
      if (this.form) this.form.addEventListener('submit', this.onFormSubmit);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.onCloseClick);
      if (this.form) this.form.removeEventListener('submit', this.onFormSubmit);
      document.removeEventListener('keydown', this.onKeydown);
    }

    open(trigger) {
      this.lastTrigger = trigger || null;
      this.removeAttribute('hidden');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        if (this.panel) this.panel.focus({ preventScroll: true });
      });
    }

    close() {
      this.classList.remove('is-open');
      const finish = () => {
        this.setAttribute('hidden', '');
        if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
          this.lastTrigger.focus();
        }
      };
      if (this.panel) {
        const onEnd = (event) => {
          if (event.target !== this.panel) return;
          this.panel.removeEventListener('transitionend', onEnd);
          finish();
        };
        this.panel.addEventListener('transitionend', onEnd);
        setTimeout(finish, 400);
      } else {
        finish();
      }
    }

    async _onFormSubmit(e) {
      e.preventDefault();
      const section = this.closest('[data-section-id]');
      if (!section) return;

      const url = plpBuildFilterUrl(e.target);
      this.close();

      const ok = await plpFetchAndSwap(url, section);
      if (ok) {
        history.pushState({ plpFilter: true }, '', url);
      } else {
        window.location.href = url;
      }
    }
  }

  customElements.define('plp-collection-drawer', PlpCollectionDrawer);
}

if (!customElements.get('plp-float-filter')) {
  class PlpFloatFilter extends HTMLElement {
    connectedCallback() {
      this._btn = this.querySelector('[data-plp-float-btn]');
      this._scrollTimer = null;
      this._onScroll = this._onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      clearTimeout(this._scrollTimer);
    }

    _onScroll() {
      if (this._btn) this._btn.classList.remove('is-visible');
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => {
        if (this._btn) this._btn.classList.add('is-visible');
      }, 50);
    }

    _onClick = (e) => {
      if (!e.target.closest('[data-plp-float-btn]')) return;
      const section = this.closest('[data-section-id]');
      if (!section) return;
      const drawer = section.querySelector('[data-plp-drawer]');
      if (!drawer || typeof drawer.open !== 'function') return;

      if (this._btn) this._btn.classList.remove('is-visible');
      drawer.open(this._btn);
      this._watchDrawerClose(drawer);
    };

    _watchDrawerClose(drawer) {
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName === 'hidden' && drawer.hasAttribute('hidden')) {
            observer.disconnect();
            setTimeout(() => {
              if (this._btn) this._btn.classList.add('is-visible');
            }, 80);
            break;
          }
        }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  customElements.define('plp-float-filter', PlpFloatFilter);
}

/* ── AJAX pill / clear-all link clicks ───────────────────────────── */

document.addEventListener('click', function (e) {
  const pill = e.target.closest('.section-plpg__pill');
  if (!pill || !pill.href) return;
  const section = pill.closest('.section-plpg[data-section-id]');
  if (!section) return;

  e.preventDefault();
  plpFetchAndSwap(pill.href, section).then((ok) => {
    if (ok) {
      history.pushState({ plpFilter: true }, '', pill.href);
    } else {
      window.location.href = pill.href;
    }
  });
});

/* ── Popstate — reload so Liquid re-renders the correct filter state ── */

window.addEventListener('popstate', function (e) {
  if (e.state && 'plpFilter' in e.state) {
    window.location.reload();
  }
});
